import { useEffect, useRef, useState } from 'react'

const STEP = 34 // 28px icon + 6px gap, matching the fixed lifecycle icons.
const clamp = (value, min, max) => Math.max(min, Math.min(max, value))
function move(order, id, index) {
  const next = order.filter(item => item !== id)
  next.splice(index, 0, id)
  return next
}

export default function WatchlistIconStrip({ lists, selectedId, onSelect, onReorder, disabled = false }) {
  const root = useRef(null)
  const gesture = useRef(null)
  const suppressClick = useRef(false)
  const [preview, setPreview] = useState(null)
  const [announcement, setAnnouncement] = useState('')
  const ids = lists.map(list => list.listId)
  const identity = JSON.stringify(ids)

  useEffect(() => {
    const cancel = () => {
      gesture.current = null
      setPreview(null)
    }
    const escape = event => {
      if (event.key === 'Escape' && gesture.current) {
        event.preventDefault()
        event.stopPropagation()
        cancel()
      }
    }
    window.addEventListener('keydown', escape, true)
    window.addEventListener('blur', cancel)
    return () => {
      window.removeEventListener('keydown', escape, true)
      window.removeEventListener('blur', cancel)
      gesture.current = null
    }
  }, [])

  useEffect(() => {
    // A refresh, deletion, account change, or pending save invalidates this gesture.
    gesture.current = null
    setPreview(null)
  }, [identity, disabled])

  function updateGesture(clientX) {
    const drag = gesture.current
    if (!drag) return
    drag.clientX = clientX
    const delta = clientX - drag.startX + drag.scroller.scrollLeft - drag.startScroll
    if (Math.abs(delta) >= 4) drag.moved = true
    if (!drag.moved) return
    const x = clamp(drag.startIndex * STEP + delta, 0, (drag.order.length - 1) * STEP)
    const index = clamp(Math.round(x / STEP), 0, drag.order.length - 1)
    drag.next = move(drag.order, drag.id, index)
    setPreview({ id: drag.id, x, order: drag.next })
  }

  useEffect(() => {
    if (!preview) return
    let frame
    let previousTime
    const tick = time => {
      const drag = gesture.current
      if (!drag) return
      const rect = drag.scroller.getBoundingClientRect()
      const direction = drag.clientX < rect.left + 24 ? -1 : drag.clientX > rect.right - 24 ? 1 : 0
      const elapsed = previousTime == null ? 0 : Math.min(32, time - previousTime)
      previousTime = time
      const before = drag.scroller.scrollLeft
      drag.scroller.scrollLeft += direction * elapsed * 0.35
      if (drag.scroller.scrollLeft !== before) updateGesture(drag.clientX)
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [Boolean(preview)])

  const finish = (event, cancelled = false) => {
    const drag = gesture.current
    if (!drag || drag.pointerId !== event.pointerId) return
    if (!cancelled) updateGesture(event.clientX)
    gesture.current = null
    setPreview(null)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    event.currentTarget.blur()
    if (!cancelled && drag.moved && drag.next.join('|') !== drag.order.join('|')) {
      onReorder(drag.next)
      setAnnouncement(`${lists.find(list => list.listId === drag.id)?.name} moved to position ${drag.next.indexOf(drag.id) + 1} of ${ids.length}`)
    }
  }

  return <div ref={root} role="group" aria-label="Custom watchlist icons"
    className="relative h-7 shrink-0" style={{ width: Math.max(0, lists.length * STEP - 6) }}>
    <span className="sr-only" aria-live="polite">{announcement}</span>
    {lists.map((list, index) => {
      const selected = selectedId === list.listId
      const dragging = preview?.id === list.listId
      const x = dragging ? preview.x : (preview ? preview.order.indexOf(list.listId) : index) * STEP
      return <button key={list.listId} type="button" aria-label={list.name} aria-pressed={selected}
        title={`${list.name} — Command-drag to reorder; Option+Shift+Left/Right when focused`}
        style={{ transform: `translate3d(${x}px, 0, 0) scale(${dragging ? 1.08 : 1})`, zIndex: dragging ? 2 : 1, transition: dragging ? 'none' : undefined }}
        className={`outline-none focus-visible:ring-1 focus-visible:ring-ink/30 absolute left-0 top-0 flex h-7 w-7 touch-pan-x select-none items-center justify-center rounded-full border border-ink/20 font-mono text-xs font-semibold uppercase transition-transform duration-200 ease-out motion-reduce:transition-none ${dragging ? 'cursor-grabbing shadow-lg' : ''} ${selected ? 'border-ink/40 bg-ink text-canvas' : 'bg-canvas text-inkMuted hover:bg-canvas2'}`}
        onPointerDown={event => {
          suppressClick.current = false
          if (!event.metaKey || event.button !== 0) return
          suppressClick.current = true
          if (disabled || lists.length < 2) return
          event.preventDefault()
          event.currentTarget.focus({ preventScroll: true })
          const scroller = root.current.parentElement
          gesture.current = { id: list.listId, pointerId: event.pointerId, startX: event.clientX, clientX: event.clientX, startIndex: index, startScroll: scroller.scrollLeft, scroller, order: ids, next: ids, moved: false }
          event.currentTarget.setPointerCapture?.(event.pointerId)
        }}
        onPointerMove={event => {
          if (gesture.current?.pointerId !== event.pointerId) return
          event.preventDefault()
          updateGesture(event.clientX)
        }}
        onPointerUp={event => finish(event)}
        onPointerCancel={event => finish(event, true)}
        onLostPointerCapture={event => finish(event, true)}
        onClick={event => {
          if ((suppressClick.current && event.detail !== 0) || event.metaKey) { suppressClick.current = false; return }
          onSelect(list.listId)
          if (event.detail) event.currentTarget.blur()
        }}
        onKeyDown={event => {
          if (!event.altKey || !event.shiftKey || event.metaKey || event.ctrlKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
          event.preventDefault()
          event.stopPropagation()
          if (disabled) return
          const nextIndex = clamp(index + (event.key === 'ArrowRight' ? 1 : -1), 0, ids.length - 1)
          if (nextIndex !== index) {
            onReorder(move(ids, list.listId, nextIndex))
            setAnnouncement(`${list.name} moved to position ${nextIndex + 1} of ${ids.length}`)
          }
        }}>
        {list.name.trim().charAt(0) || '?'}
      </button>
    })}
  </div>
}
